import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, FlexControlledForm, useDebounceState } from '@common-fe/common-fe';
import _ from 'lodash';

import REGEXP from '@/common/regexp';
import routes from '@/common/routes';
import { MFAError, MfaServerErrorText, MFAStep } from '@/modules/user/components/SignIn/MultiFactorAuthentication/mfa.types';
import useMFAStore from '@/modules/user/components/SignIn/MultiFactorAuthentication/stores/useMFA.store';
import ResendCodeButton from '@/modules/user/components/Verification/components/ResendCodeButton';
import VerificationCodeModal from '@/modules/user/components/Verification/components/VerificationCodeModal';
import useVerificationCodeFields, { FormValues } from '@/modules/user/components/Verification/hooks/useVerificationCodeFields';
import { useValidateForm } from '@/utils/hooks';

import useMFAChannelsDescription from '../hooks/useMFAChannelsDescription';
import useVerifyMFACodeQuery from '../queries/useVerifyMFACode.query';

import OpenChangeVerificationMethodModalButton from './OpenChangeVerificationMethodModalButton';
import RememberMeCheckbox from './RememberMeCheckbox';

interface Props {
  onSetVisible: (value: boolean) => void;
  setStep: (step: MFAStep) => void;
  onResendCode: () => void;
  onShowAccountLockedModal: (value: number) => void;
  resendTimeout: number;
  sendCodeErrorText?: string;
}

const SendCodeModal: React.FC<Props> = ({
  onSetVisible,
  setStep,
  onResendCode,
  onShowAccountLockedModal,
  resendTimeout,
  sendCodeErrorText,
}) => {
  const { push } = useHistory();
  const { activeChannel, session, isRememberMe, userName, isRequiredExpressSetup } = useMFAStore();
  const [verificationErrorText, setVerificationErrorText] = useState('');
  const fields = useVerificationCodeFields({ verificationErrorText });
  const [state, handleSetState] = useState<FormValues>();
  const { values, setValues } = useDebounceState<FormValues>();
  const { handlePreSubmit, hasErrors } = useValidateForm(fields);
  const channelDescription = useMFAChannelsDescription(activeChannel?.type);

  const { verifyMFACodeAndSignIn, isLoading } = useVerifyMFACodeQuery({ isRequiredExpressSetup });

  const verificationCode = useMemo(() => state?.verificationCode?.replace(REGEXP.SPACE_SYMBOL, '') || '', [state]);

  const handleConfirm = useCallback(async () => {
    try {
      const isSuccessValidation = handlePreSubmit(state);
      if (isSuccessValidation && activeChannel?.type) {
        setVerificationErrorText('');
        await verifyMFACodeAndSignIn({
          session: session || '',
          code: verificationCode,
          communicationType: activeChannel?.type,
          isRememberMe,
        }, userName);
        push(routes.HOME);
      }
    } catch (error) {
      const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;
      const lockingLeft = _.get(error, 'response.data.locking_left', 10) as number;

      if (errorMessage?.includes(MfaServerErrorText.USER_ACCOUNT_IS_LOCKED)) {
        onShowAccountLockedModal(lockingLeft);
        return;
      }

      if (errorMessage?.includes(MfaServerErrorText.SESSION_IS_EXPIRED)) {
        onSetVisible(false);
        return;
      }

      setVerificationErrorText(MFAError.CODE_IS_INVALID);
    }
  }, [
    state,
    verificationCode,
    session,
    isRememberMe,
    userName,
    handlePreSubmit,
    verifyMFACodeAndSignIn,
    onShowAccountLockedModal,
    onSetVisible,
    push,
    activeChannel?.type
  ]);

  const handleOpenChangeVerificationMethodModal = useCallback(() => {
    setStep(MFAStep.CHANGE_VERIFICATION_METHOD);
  }, [setStep]);

  useEffect(() => {
    if (values) {
      handleSetState(values);
      setVerificationErrorText('');
    }
  }, [handleSetState, values]);

  useEffect(() => {
    if (verificationCode?.length === 6) {
      handleConfirm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode]);

  return (
    <VerificationCodeModal
      testId="verification-code-modal"
      icon={channelDescription.icon}
      title={channelDescription.name}
      value={activeChannel?.value || ''}
      errorText={sendCodeErrorText}
      isLoading={isLoading}
      onSetVisible={onSetVisible}
      onConfirm={handleConfirm}
      {...isRequiredExpressSetup
        ? {}
        : { footerNode: <RememberMeCheckbox testId="change-verification-method-modal" /> }
      }
      form={(
        <FlexControlledForm
          fields={fields}
          onChangeValues={setValues}
          formStyle={{ margin: '24px' }}
          showError={hasErrors || !!verificationErrorText}
          editMode
        >
          <Box width="320px" alignSelf="end">
            <ResendCodeButton
              testId="verification-code-modal"
              key={activeChannel?.value}
              onResend={onResendCode}
              defaultRemainingTime={resendTimeout}
            />
            <Box margin={{ top: 'spacing12' }}>
              <OpenChangeVerificationMethodModalButton
                testId="verification-code-modal"
                onClick={handleOpenChangeVerificationMethodModal}
              />
            </Box>
          </Box>
        </FlexControlledForm>
      )}
    />
  );
};

export default SendCodeModal;
